import { Fragment, HTMLProps, useEffect, useRef, useState } from "react";

import date from "date-and-time";
import { useNavigate } from "react-router-dom";

import { CameraIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { StarIcon } from '@heroicons/react/24/solid';
import { createColumnHelper, flexRender, getCoreRowModel, getExpandedRowModel, getPaginationRowModel, getSortedRowModel, SortingState, useReactTable } from "@tanstack/react-table";

import useVehiclesMutations from "../../data/useVehiclesMutations";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import Modal from "../../layouts/Modal";
import { ILead } from "../../models/Lead";
import { PartsStatusValues } from "../../models/Vehicle";
import classNames from "../../utils/classNames";
import Pagination from "../shared/Tables/Pagination";
import PriceFormat from "../../utils/priceFormat";
import YardStatusDropdown from "../awaitingInventory/YardStatusDropdown";
import { OfferType } from "../../models/EnginesWorldwide";
import EnginesWorldWideOffers from "./EnginesWorldWideOffers";
import AwaitingInventoryStatusDropdown from "../awaitingInventory/AwaitingInventoryStatusDropdown";
import ViewVideoButton from "./ViewVideoButton";
import openInNewTab from "../../utils/openInNewTab";
import VehicleLocationDropdown from "../vehicles/VehicleLocationDropdown";
import VehicleIcons from "../shared/VehicleIcons/VehicleIcons";
import PartsPriorityDropdown from "../awaitingInventory/PartsPriorityDropdown";
import { truncate } from "lodash";
import TableCheckboxCol from "../shared/Tables/IndeterminateCheckbox";

function IndeterminateCheckbox({
  indeterminate,
  className = '',
  ...rest
}: { indeterminate?: boolean } & HTMLProps<HTMLInputElement>) {
  const ref = useRef<HTMLInputElement>(null!)

  useEffect(() => {
    if (typeof indeterminate === 'boolean') {
      ref.current.indeterminate = !rest.checked && indeterminate
    }
  }, [ref, indeterminate])

  return (
    <input
      type="checkbox"
      ref={ref}
      className={className + 'p-2 cursor-pointer'}
      {...rest}
    />
  )
}

const colHelper = createColumnHelper<ILead>();

const cols = [
  TableCheckboxCol(true),
  colHelper.display({
    id: "image",
    cell: (info) => (
      <span className="flex justify-center relative" >
        {info.row.original.vehicle?.vehicleImageUrl ? (
          <>
            <img
              className="w-32 h-20 object-contain"
              src={`${info.row.original.vehicle != null ? info.row.original.vehicle.vehicleImageUrl : null}`}
            />
            {info.row.original.vehicle?.vehiclePremium && (
              <StarIcon className="w-8 h-8 text-amber-500 absolute -top-2 -right-2" />
            )}

          </>
        ) : (
          <CameraIcon className="w-14 text-gray-600" />
        )}
      </span>
    ),
  }),
  colHelper.accessor("vehicle.combinedMake", {
    header: "Make & Model",
    enableSorting: false,
    cell: (info) => <div>
      <p>{info.row.original.vehicle?.combinedMake}</p>
      <p>{info.row.original.vehicle?.combinedModel}</p>
    </div>,
  }),
  colHelper.accessor("vehicle.vrm", {
    header: "VRM",
    enableSorting: false,
  }),
  colHelper.accessor("vehicle.engineModelCode", {
    header: "Engine Code",
    enableSorting: false,
  }),
  colHelper.accessor("vehicle.odometerInMiles", {
    header: "Mileage & Body Type",
    enableSorting: false,
    cell: (info) => <div>
      <p>{info.row.original.vehicle?.odometerInMiles}</p>
      <p>{info.row.original.vehicle?.bodyStyleDescription}</p>
    </div>,
  }),
  // colHelper.accessor("vehicle.vehicleStarting", {
  //   header: "Starting",
  //   enableSorting: false,
  //   cell: (info) => <span className="flex justify-center text-xl">{info.getValue() ? "✔" : "✘"}</span>,
  // }),
  colHelper.accessor("vehicle", {
    header: "Vehicle",
    enableSorting: false,
    cell: (info) => <VehicleIcons vehicle={info.row.original.vehicle!} />,
  }),
  colHelper.accessor("vehicle.ebayEnginePrice", {
    header: "Ebay Engine £",
    cell: (info) => <span>{PriceFormat(info.getValue())}</span>,
    // cell: EditableCell,
  }),
  colHelper.accessor("vehicle.enginesWorldwideEngineOffer", {
    header: "Export Offers £",
    cell: (info) => <div>
      <p>Engine Offer</p>
      <p>{PriceFormat(info.getValue())}</p>
      <p className="mt-2 ">VFE Offer</p>
      <p>{PriceFormat(info.row.original.vehicle?.enginesWorldwideVfeOffer)}</p>
    </div>
  }),
  colHelper.accessor("vehicle.collectedOn", {
    header: "Collected?",
    sortingFn: "auto",
    cell: (info) => <span className="flex justify-center text-xl">{info.getValue() ? "✔" : "✘"}</span>,
  }),
  colHelper.accessor("vehicle.enginesWorldwide", {
    header: "Engines Worldwide",
    sortingFn: "auto",
    cell: (info) => <EnginesWorldWideOffers offers={info.getValue()} />
  }),
  colHelper.accessor("vehicle.yardStatus", {
    header: "Status",
    enableSorting: false,
    cell: (info) => <div id={info.row.original.id}>
      <p>Parts Status</p>
      <AwaitingInventoryStatusDropdown vehicle={info.row.original.vehicle!} classes="h-8 w-40 text-sm" />
      <p className="mt-2">Location</p>
      <VehicleLocationDropdown vehicle={info.row.original.vehicle!} classes="h-8 w-40 text-sm" />
      <p className="mt-2">Yard Status</p>
      <YardStatusDropdown vehicle={info.row.original.vehicle!} classes="h-8 w-40 text-sm" />
      <p className="mt-2">Parts Priority Status</p>
      <PartsPriorityDropdown vehicle={info.row.original.vehicle!} classes="h-8 w-40 text-sm" />
    </div>
  }),
];


export default function AwaitingArrivalTable({ leads }: { leads: ILead[] | undefined }) {
  const navigate = useNavigate();
  const { bulkUpdate } = useVehiclesMutations();
  const { saveHandlers } = useDefaultCRUDHandlers("Leads");
  const [updateOpen, setUpdateOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("0");

  const [sorting, setSorting] = useState<SortingState>([]);
  // const [sorting, setSorting] = useState<SortingState>([{ id: "vehicle.scheduledCollectionOn", desc: true }]);

  const handleRowUpdate = () => {
    var vehicleIds = new Array();

    table.getSelectedRowModel().flatRows.forEach(lead => {
      vehicleIds.push(lead.original.vehicle?.id);
    });

    const body = {
      engineExportStatus: parseInt(selectedStatus as keyof PartsStatusValues),
      vehicles: vehicleIds
    };

    bulkUpdate.mutate(body, {
      onSuccess: () => {
        table.resetRowSelection();
        saveHandlers.onSuccess();
        setUpdateOpen(false);
      },
      onError: saveHandlers.onError
    });
  };

  const getRowClasses = (rowData: ILead) => {
    let classes = "";

    if (rowData.vehicle?.collectedOn) {
      classes = "bg-green-200 ";
    } else if (rowData.vehicle?.isDropOff) {
      classes = "bg-orange-100 ";
    }

    return classes;
  }

  const table = useReactTable({
    data: leads!,
    columns: cols,
    state: {
      sorting: sorting,
    },
    enableRowSelection: true,
    getPaginationRowModel: getPaginationRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  useEffect(() => {
    table.setPageSize(50);
  }, [])

  return (
    <>
      <div className="flex justify-between">
        {/* <div className="space-x-4">
          <button
            className={classNames(table.getSelectedRowModel().flatRows.length === 0 ? "bg-gray-200" : "bg-primary-600 hover:bg-primary-700", "mt-4 inline-flex items-center rounded-md border border-transparent px-3 py-2 text-sm font-medium leading-4 text-white shadow-sm")}
            type="button"
            disabled={table.getSelectedRowModel().flatRows.length === 0}
            onClick={() => setUpdateOpen(true)}
          >
            Bulk Update
          </button>
          <button
            className={"bg-primary-600 hover:bg-primary-700 mt-4 inline-flex items-center rounded-md border border-transparent px-3 py-2 text-sm font-medium leading-4 text-white shadow-sm"}
            type="button"
            onClick={() => navigate("/parts-manager/vehicle-lookup")}
          >
            Vehicle Lookup
          </button>
        </div> */}

        {/* <div className="flex justify-center items-center">
          {table.getPreFilteredRowModel().rows.length} Cars Shown
        </div> */}

      </div>
      <div className="mt-4 bg-white overflow-auto">
        <table className="min-w-full overflow-scroll divide-y border divide-gray-300">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="bg-primary-50 text-gray-800">
                {headerGroup.headers.map((header, i) => (
                  <th
                    key={header.id}
                    scope="col"
                    className="py-3.5 px-2 text-sm font-semibold lg:table-cell text-left whitespace-pre"
                  >
                    {header.isPlaceholder
                      ? null
                      : (
                        <div
                          {...{
                            className: header.column.getCanSort()
                              ? 'cursor-pointer select-none'
                              : '',
                            onClick: header.column.getToggleSortingHandler(),
                          }}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          {{
                            asc: ' ▲',
                            desc: ' ▼',
                          }[header.column.getIsSorted() as string] ?? null}
                        </div>

                      )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row, i) => {
              let rowCells = row.getVisibleCells();
              return (
                <Fragment key={i}>
                  <tr
                    key={row.id}
                    onClick={() => openInNewTab(`leads/${row.original.id}/parts`)}
                    className={classNames(getRowClasses(row.original), "hover:bg-gray-100 hover:cursor-pointer")}
                  >
                    {rowCells.map((cell) => (
                      <td key={cell.id} className="px-2 py-2 border text-sm">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                  {row.getIsExpanded() && (
                    <tr>
                      {/* 2nd row is a custom 1 cell row */}
                      <td colSpan={row.getVisibleCells().length} className="bg-gray-100 pl-28 p-4">
                        <div className="flex gap-x-10">

                          <ViewVideoButton lead={row.original} />

                          <div className="flex flex-col">
                            <span className="font-bold">Vehicle Notes:</span>
                            <span>{row.original.vehicle?.notes}</span>
                          </div>

                          <div className="flex flex-col">
                            <span className="font-bold">Office Notes:</span>
                            <span>{row.original.officeNotes}</span>
                          </div>

                          <div className="flex flex-col">
                            <span className="font-bold">Driver Notes:</span>
                            <span>{row.original.driverNotes}</span>
                          </div>

                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>

        <Pagination table={table} />

      </div>
    </>
  );
}